import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppHeader from "@/components/app/AppHeader";
import Sparkline from "@/components/app/Sparkline";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Globe2, ArrowRight, Trash2, TrendingUp, TrendingDown, Minus, Flame, Share2 } from "lucide-react";
import { computeStreak } from "@/lib/streak";
import { milestoneToastsEnabled } from "@/lib/local-prefs";
import StreakShareCard from "@/components/app/StreakShareCard";
import LatestPublishes from "@/components/app/LatestPublishes";
import PlanLockedCard from "@/components/app/PlanLockedCard";
import SimpleDashboard from "@/pages/SimpleDashboard";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, refresh: refreshUser } = useAuth();
  const [projects, setProjects] = useState(null);
  const [scores, setScores] = useState({}); // project_id -> {latest_score, sparkline}
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [shareProj, setShareProj] = useState(null); // {domain, name, streak, score} | null

  const load = async () => {
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load projects");
      setProjects([]);
    }
  };
  const loadScores = async () => {
    try {
      const { data } = await api.get("/projects/scores-summary?weeks=26");
      const map = {};
      for (const row of data || []) map[row.project_id] = row;
      setScores(map);
    } catch (e) {
      // Sparkline is non-critical — fail silently.
    }
  };
  useEffect(() => { load(); loadScores(); }, []);

  // Quick-start pickup — if the user landed here from /register?from=quick-start
  // with a pending payload stashed in sessionStorage, finish the create-project
  // call now that they're logged in, then redirect to the new project page.
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    let pending;
    try {
      const raw = sessionStorage.getItem("arloseo_quick_start_pending");
      if (!raw) return;
      pending = JSON.parse(raw);
    } catch {
      sessionStorage.removeItem("arloseo_quick_start_pending");
      return;
    }
    if (!pending?.business_slug) return;
    sessionStorage.removeItem("arloseo_quick_start_pending");
    (async () => {
      try {
        const { data } = await api.post("/quick-start", pending);
        toast.success(`Welcome — tracking ${data.keywords_tracked} keyword${data.keywords_tracked === 1 ? "" : "s"} for ${data.project_name}.`);
        navigate(`/app/projects/${data.project_id}`);
      } catch (e) {
        toast.error(`Quick-start resume failed: ${formatApiError(e)}`);
      }
    })();
  }, [user, navigate]);

  // One-time milestone toast: when a project's streak crosses a new milestone
  // bucket (3W, 6W, 12W, 24W, 52W), nudge the user to share. Per-project
  // ratchet in localStorage so we never re-toast the same milestone.
  const MILESTONES = [3, 6, 12, 24, 52];
  useEffect(() => {
    if (!projects || projects.length === 0) return;
    if (Object.keys(scores).length === 0) return;
    if (!milestoneToastsEnabled()) return;
    for (const p of projects) {
      const s = scores[p.id];
      if (!s) continue;
      const streak = computeStreak(s.sparkline || []);
      // Highest milestone the user has currently reached.
      const reached = MILESTONES.filter((m) => streak >= m);
      if (reached.length === 0) continue;
      const highest = reached[reached.length - 1];
      const key = `arloseo.streak_seen.${p.id}`;
      const seen = parseInt(localStorage.getItem(key) || "0", 10);
      if (highest <= seen) continue;
      localStorage.setItem(key, String(highest));
      toast.success(`🔥 ${streak}-week streak on ${p.domain}`, {
        description: "Milestone unlocked — celebrate the streak.",
        duration: 12000,
        action: {
          label: "Share →",
          onClick: () =>
            setShareProj({
              domain: p.domain,
              name: p.name,
              streak,
              score: s.latest_score,
            }),
        },
      });
    }
  }, [projects, scores]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/projects", { name, url });
      setProjects((p) => [data, ...(p || [])]);
      setOpen(false); setName(""); setUrl("");
      toast.success("Project created");
      navigate(`/app/projects/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!confirm("Delete this project and all its data?")) return;
    try {
      await api.delete(`/projects/${id}`);
      setProjects((p) => p.filter((x) => x.id !== id));
      toast.success("Project deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  async function switchUiMode(mode) {
    try {
      await api.post("/auth/me/ui-mode", { ui_mode: mode });
      await refreshUser();
      toast.message(mode === "simple" ? "Switched to simple mode" : "Switched to advanced mode");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  // SMB users land on Simple Mode by default. Pro mode = full dashboard below.
  if (user && typeof user === "object" && user.ui_mode === "simple") {
    return <SimpleDashboard user={user} onSwitchToPro={() => switchUiMode("pro")} />;
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="px-4 md:px-8 py-10 max-w-7xl mx-auto">
        <div className="flex items-end justify-between gap-4 mb-10">
          <div>
            <p className="font-mono-data text-xs uppercase tracking-widest text-muted-foreground">// workspace</p>
            <h1 className="font-semibold tracking-tight text-4xl md:text-5xl mt-2">Projects.</h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-xl">
              Each project tracks one website. Audit URLs, track keywords, study competitors and generate AI strategy.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <Button
              variant="ghost"
              size="sm"
              className="rounded-none text-muted-foreground hover:text-foreground"
              onClick={() => switchUiMode("simple")}
              data-testid="dashboard-switch-to-simple"
            >
              Simple mode
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-none bg-foreground hover:bg-foreground/90 h-11 px-5" data-testid="new-project-button">
                <Plus className="w-4 h-4 mr-2" /> New project
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight text-2xl">Add a project</DialogTitle>
                <DialogDescription>One project per website. You can rename it any time.</DialogDescription>
              </DialogHeader>
              <form onSubmit={create} className="space-y-4 mt-2" data-testid="new-project-form">
                <div className="space-y-2">
                  <Label className="font-mono-data text-xs uppercase tracking-widest">Project name</Label>
                  <Input value={name} onChange={(e)=>setName(e.target.value)} required
                    className="rounded-none h-11 border-foreground/30 focus-visible:ring-foreground"
                    data-testid="new-project-name" placeholder="Acme Marketing Site" />
                </div>
                <div className="space-y-2">
                  <Label className="font-mono-data text-xs uppercase tracking-widest">Website URL</Label>
                  <Input value={url} onChange={(e)=>setUrl(e.target.value)} required
                    className="rounded-none h-11 border-foreground/30 focus-visible:ring-foreground"
                    data-testid="new-project-url" placeholder="https://example.com" />
                </div>
                <DialogFooter className="gap-2">
                  <Button type="button" variant="outline" className="rounded-none" onClick={()=>setOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={busy} className="rounded-none bg-foreground hover:bg-foreground/90" data-testid="new-project-submit">
                    {busy ? "Creating…" : "Create project"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <PlanLockedCard user={user} />

        {projects === null ? (
          <div className="text-sm font-mono-data text-muted-foreground">Loading…</div>
        ) : projects.length === 0 ? (
          <div className="border border-dashed border-foreground/30 p-12 text-center" data-testid="empty-projects-state">
            <div className="w-14 h-14 mx-auto bg-foreground grid place-items-center text-background mb-4">
              <Plus className="w-7 h-7" strokeWidth={1.5} />
            </div>
            <h3 className="font-display tracking-tight text-2xl">No projects yet.</h3>
            <p className="text-sm text-muted-foreground mt-1">Add your first website to start tracking SEO wins, keywords, and content opportunities.</p>
            <Button onClick={()=>setOpen(true)} className="mt-6 rounded-none bg-foreground hover:bg-foreground/90" data-testid="empty-new-project-button">
              <Plus className="w-4 h-4 mr-2" /> Add your first site
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 -mr-px -mb-px" data-testid="projects-grid">
            {projects.map((p) => (
              <div key={p.id} className="border border-border bg-white -mr-px -mb-px p-6 group tilt-hover" data-testid={`project-card-${p.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-xl tracking-tight truncate">{p.name}</h3>
                    <p className="font-mono-data text-xs text-muted-foreground truncate">{p.domain}</p>
                  </div>
                  <button
                    onClick={()=>remove(p.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                    data-testid={`project-delete-${p.id}`}
                    aria-label="Delete project"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-3 mt-5 font-mono-data text-xs">
                  <div><div className="text-muted-foreground">DA</div><div className="text-foreground text-base mt-0.5">{p.metrics?.domain_authority ?? "—"}</div></div>
                  <div><div className="text-muted-foreground">KEYWORDS</div><div className="text-foreground text-base mt-0.5">{(p.metrics?.organic_keywords ?? 0).toLocaleString()}</div></div>
                  <div><div className="text-muted-foreground">TRAFFIC</div><div className="text-foreground text-base mt-0.5">{(p.metrics?.organic_traffic ?? 0).toLocaleString()}</div></div>
                </div>
                {(() => {
                  const s = scores[p.id];
                  const points = s?.sparkline || [];
                  const hasHistory = points.length >= 2;
                  const latest = s?.latest_score;
                  const first = points[0]?.score;
                  const delta = (hasHistory && typeof latest === "number" && typeof first === "number") ? latest - first : null;
                  const streak = computeStreak(points);
                  let TrendIcon = Minus, trendCls = "text-muted-foreground";
                  if (delta !== null && delta > 0) { TrendIcon = TrendingUp; trendCls = "text-emerald-600"; }
                  else if (delta !== null && delta < 0) { TrendIcon = TrendingDown; trendCls = "text-rose-600"; }
                  const sparkColor = delta === null || delta === 0 ? "#71717a" : (delta > 0 ? "#059669" : "#e11d48");
                  return (
                    <div className="mt-5 pt-4 border-t border-dashed border-foreground/15" data-testid={`project-sparkline-${p.id}`}>
                      <div className="flex items-center justify-between gap-3 mb-1">
                        <div className="flex items-center gap-2">
                          <div className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
                            26-WEEK SCORE TREND
                          </div>
                          {streak >= 2 && (
                            <span
                              className="inline-flex items-center gap-1 border border-amber-500/40 bg-amber-50 px-1.5 py-0.5 font-mono-data text-[10px] font-semibold uppercase tracking-widest text-amber-700"
                              data-testid={`project-streak-${p.id}`}
                              title={`${streak} weeks improving in a row`}
                            >
                              <Flame className="w-3 h-3" strokeWidth={2.5} fill="currentColor" />
                              {streak}W STREAK
                            </span>
                          )}
                          {streak >= 3 && typeof latest === "number" && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setShareProj({ domain: p.domain, name: p.name, streak, score: latest });
                              }}
                              data-testid={`project-streak-share-${p.id}`}
                              title="Share milestone"
                              className="inline-flex items-center justify-center border border-foreground/30 hover:border-foreground hover:bg-foreground hover:text-background transition-colors px-1.5 py-0.5 font-mono-data text-[10px]"
                            >
                              <Share2 className="w-3 h-3" strokeWidth={2.5} />
                            </button>
                          )}
                        </div>
                        {hasHistory && typeof latest === "number" && (
                          <div className="flex items-center gap-1.5 font-mono-data text-xs">
                            <span className="text-foreground font-semibold">{latest}</span>
                            <span className="text-muted-foreground">/100</span>
                            {delta !== null && (
                              <span className={`inline-flex items-center gap-0.5 ${trendCls}`}>
                                <TrendIcon className="w-3 h-3" strokeWidth={2.5} />
                                {delta > 0 ? `+${delta}` : delta}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <Sparkline data={points} color={sparkColor} height={32} />
                    </div>
                  );
                })()}
                <Link to={`/app/projects/${p.id}`} className="mt-6 inline-flex items-center gap-2 text-sm font-medium hover:underline underline-offset-4" data-testid={`project-open-${p.id}`}>
                  Open dashboard <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            ))}
          </div>
        )}

        {projects && projects.length > 0 && (
          <div className="mt-12">
            <LatestPublishes limit={25} />
          </div>
        )}
      </main>
      <StreakShareCard
        open={!!shareProj}
        onOpenChange={(o) => { if (!o) setShareProj(null); }}
        domain={shareProj?.domain}
        name={shareProj?.name}
        streak={shareProj?.streak}
        score={shareProj?.score}
      />
    </div>
  );
}
